import { css } from 'aphrodite';
import * as React from 'react';
import type { Scenario, SkillArea } from '../../../voice-agent/types/scenarios';
import { Button } from '../../../../web/components-v2/Button';
import { ScenarioRow } from './ScenarioRow';
import { styleSheet } from './styles';

/**
 * Props for the SkillAreaRow component
 */
export interface SkillAreaRowProps {
	/** The skill area to display */
	skillArea: SkillArea;
	/** Scenarios belonging to this skill area */
	scenarios: Scenario[];
	/** Whether this skill area row is expanded */
	isExpanded: boolean;
	/** Callback when the skill area expansion is toggled */
	onToggle: () => void;
	/** Callback when the edit button is clicked */
	onEdit: () => void;
	/** Callback when the delete button is clicked */
	onDelete: () => void;
	/** Callback when the add scenario button is clicked */
	onAddScenario: () => void;
	/** Callback when a scenario edit button is clicked */
	onEditScenario: (scenario: Scenario) => void;
	/** Callback when a scenario delete button is clicked */
	onDeleteScenario: (scenario: Scenario) => void;
}

/**
 * SkillAreaRow component
 *
 * Renders a single skill area row with its nested scenarios.
 * The skill area row displays the name, description, active status badge,
 * count of scenarios, and action buttons.
 *
 * When expanded, it shows nested scenario rows with their details and actions.
 */
export const SkillAreaRow: React.FC<SkillAreaRowProps> = ({
	skillArea,
	scenarios,
	isExpanded,
	onToggle,
	onEdit,
	onDelete,
	onAddScenario,
	onEditScenario,
	onDeleteScenario,
}) => {
	/**
	 * Handle keyboard navigation for the skill area row
	 */
	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onToggle();
			}
		},
		[onToggle]
	);

	return (
		<div>
			{/* Skill area row */}
			<div className={css(styleSheet.row, styleSheet.skillAreaRow)}>
				<div
					className={css(styleSheet.chevronContainer)}
					onClick={onToggle}
					onKeyDown={handleKeyDown}
					role='button'
					tabIndex={0}
					aria-expanded={isExpanded}
					aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${skillArea.name}`}
				>
					<svg
						className={css(styleSheet.chevron, isExpanded && styleSheet.chevronExpanded)}
						viewBox='0 0 24 24'
						aria-hidden='true'
					>
						<path d='M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z' />
					</svg>
				</div>
				<div className={css(styleSheet.rowContent)}>
					<div className={css(styleSheet.rowName)}>{skillArea.name}</div>
					{skillArea.description && <div className={css(styleSheet.rowDescription)}>{skillArea.description}</div>}
				</div>
				<div
					className={css(
						styleSheet.statusBadge,
						skillArea.active ? styleSheet.statusActive : styleSheet.statusInactive
					)}
				>
					{skillArea.active ? 'Active' : 'Inactive'}
				</div>
				<div className={css(styleSheet.countBadge)}>{scenarios.length} scenarios</div>
				<div className={css(styleSheet.actions)}>
					<Button variant='secondary' size='sm' onClick={onAddScenario}>
						+ Scenario
					</Button>
					<Button variant='secondary' size='sm' onClick={onEdit}>
						Edit
					</Button>
					<Button variant='destructive' size='sm' onClick={onDelete}>
						Delete
					</Button>
				</div>
			</div>

			{/* Nested scenarios (if expanded) */}
			{isExpanded && (
				<div className={css(styleSheet.nestedContent)}>
					{scenarios.map(scenario => (
						<ScenarioRow
							key={scenario.id}
							scenario={scenario}
							onEdit={() => onEditScenario(scenario)}
							onDelete={() => onDeleteScenario(scenario)}
						/>
					))}
					{scenarios.length === 0 && (
						<div className={css(styleSheet.row, styleSheet.scenarioRow)}>
							<div className={css(styleSheet.chevronPlaceholder)} />
							<div className={css(styleSheet.rowContent)}>
								<div className={css(styleSheet.rowDescription)}>No scenarios yet</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
